import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { applyCustomerCredit } from '../../lib/customers-api';
import { consoleZoneLabel, zoneByCode } from '../../lib/zones';
import { useToast } from '../../components/ToastProvider';
import { actionErrorToast } from '../../lib/friendly-error';

/**
 * Add Credit modal (PL36, ADR-086 D4 / the 10 July 2026 note).
 *
 * Grants a goodwill CREDIT to the customer's balance. The amount is EX-TAX — GST
 * is added on top server-side by applyCredit (ADR-083 D4); the platform never
 * shows a fabricated GST figure here. The route enforces the acting operator's
 * monthly consultant budget with a HARD block; that block surfaces as a friendly
 * toast, never a raw error (error-surfacing standard — useToast / toastFromError).
 */

interface Props {
  accountId: string;
  /** ADR-120 — the customer's own zone; the credit must be applied where the account lives. */
  zone?: string;
  businessName: string;
  onClose: () => void;
  onApplied: () => Promise<void> | void;
}

const AmountSchema = z
  .number()
  .positive('Amount must be greater than $0.')
  .max(100000, 'Amount looks too large — check the figure.');
const ReasonSchema = z.string().min(3, 'A short reason is required (min 3 chars).').max(500, '500 chars maximum');

export function AddCreditModal({ accountId, businessName, onClose, onApplied, zone}: Props) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFieldError(null);

    const amountSub = Number(amount);
    if (!amount.trim() || !Number.isFinite(amountSub)) {
      setFieldError('Enter an amount in dollars.');
      return;
    }
    const amtParsed = AmountSchema.safeParse(amountSub);
    if (!amtParsed.success) {
      setFieldError(amtParsed.error.issues[0]?.message ?? 'Invalid amount');
      return;
    }
    const reasonParsed = ReasonSchema.safeParse(reason.trim());
    if (!reasonParsed.success) {
      setFieldError(reasonParsed.error.issues[0]?.message ?? 'Invalid reason');
      return;
    }

    setBusy(true);
    try {
      await applyCustomerCredit(accountId, amtParsed.data, reasonParsed.data, zoneByCode(zone));
      toast.push({
        variant: 'success',
        title: 'Credit applied',
        detail: `$${amtParsed.data.toFixed(2)} (ex-GST) credited to ${businessName}.`,
      });
      await onApplied();
      onClose();
    } catch (err) {
      // What failed, where, why, and a reference (6 October 2026). The budget hard-block's
      // server-authored message is carried on the error's details and used as the reason.
      // (The parser this replaced looked for a JSON body inside the error message, which has
      // carried only the code since #21 — so the server's own budget wording never reached it.)
      toast.push({
        variant: 'error',
        ...actionErrorToast(err, { action: 'Add credit', subject: businessName, zoneLabel: consoleZoneLabel(zoneByCode(zone)!.code) }),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="portal-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Add credit"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="portal-modal">
        <button
          type="button"
          className="portal-modal-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
        >
          ×
        </button>
        {/* D9 — the zone the credit lands in, named in the dialog's title. */}
        <h2>Add credit in {consoleZoneLabel(zoneByCode(zone)!.code)}</h2>
        <p className="portal-help">
          Apply a goodwill credit to <strong>{businessName}</strong>&rsquo;s balance. Drawn against
          your monthly credit budget.
        </p>

        <form onSubmit={submit} aria-busy={busy} className="portal-wizard-form" noValidate>
          <label>
            Amount (ex-GST, USD)
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="50.00"
            />
          </label>
          <label>
            Reason (audited)
            <textarea
              required
              minLength={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Goodwill — onboarding delay"
            />
          </label>
          <p className="portal-help">
            The amount is ex-GST — GST is added at checkout, so no GST figure is shown here.
          </p>

          {fieldError && <span className="portal-field-error">{fieldError}</span>}

          <div className="portal-wizard-actions">
            <button type="submit" disabled={busy} className="portal-btn">
              {busy ? 'Applying…' : 'Apply credit'}
            </button>
            <button type="button" onClick={onClose} disabled={busy} className="portal-link-button-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
